import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import VisibilityIcon from "@material-ui/icons/Visibility";

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
  },
  headerCell: {
    fontWeight: 600,
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
  mono: {
    fontFamily: "monospace",
    fontSize: "0.82rem",
  },
  chip: {
    margin: theme.spacing(0.25),
    fontSize: "0.75rem",
    height: 22,
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  versionSummary: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
  },
}));

function formatRelative(dateVal) {
  if (!dateVal) return "—";
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

export default function GuardDevicesTable({ devices = [], versionDistribution = [], onSelectDevice }) {
  const classes = useStyles();

  return (
    <>
      {versionDistribution.length > 0 && (
        <div className={classes.versionSummary}>
          {versionDistribution.slice(0, 8).map(({ version, count }) => (
            <Chip
              key={version}
              label={`${version} (${count})`}
              size="small"
              className={classes.chip}
              variant="outlined"
            />
          ))}
        </div>
      )}

      {devices.length === 0 ? (
        <Paper className={classes.emptyState}>
          <Typography variant="body1">No guard devices found.</Typography>
          <Typography variant="body2" color="textSecondary">
            Enrolled Ouro Guard devices will appear here once heartbeats are received.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} className={classes.container}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>User</TableCell>
                <TableCell className={classes.headerCell}>Device ID</TableCell>
                <TableCell className={classes.headerCell}>Platform</TableCell>
                <TableCell className={classes.headerCell}>Version</TableCell>
                <TableCell className={classes.headerCell}>Posture</TableCell>
                <TableCell className={classes.headerCell} align="center">Attested</TableCell>
                <TableCell className={classes.headerCell}>Last Heartbeat</TableCell>
                <TableCell className={classes.headerCell} align="center">Status</TableCell>
                <TableCell className={classes.headerCell} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((device) => (
                <TableRow key={device.deviceId} hover>
                  <TableCell className={classes.mono}>{device.userId || "—"}</TableCell>
                  <TableCell className={classes.mono}>
                    {device.deviceId ? device.deviceId.slice(0, 18) + "…" : "—"}
                  </TableCell>
                  <TableCell>{device.platform || "—"}</TableCell>
                  <TableCell className={classes.mono}>{device.clientVersion || "—"}</TableCell>
                  <TableCell>{device.postureTier || "—"}</TableCell>
                  <TableCell align="center">
                    {device.attested ? (
                      <Chip label="yes" size="small" className={classes.chip} style={{ backgroundColor: "#43a047", color: "#fff" }} />
                    ) : (
                      <Chip label="no" size="small" className={classes.chip} />
                    )}
                  </TableCell>
                  <TableCell>{formatRelative(device.lastHeartbeatAt)}</TableCell>
                  <TableCell align="center">
                    {device.revoked ? (
                      <Chip label="revoked" size="small" className={classes.chip} style={{ backgroundColor: "#e53935", color: "#fff" }} />
                    ) : device.forkSuspected ? (
                      <Chip label="fork" size="small" className={classes.chip} style={{ backgroundColor: "#ff6f00", color: "#fff" }} />
                    ) : (
                      <Chip label="ok" size="small" className={classes.chip} style={{ backgroundColor: "#1976d2", color: "#fff" }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Inspect device">
                      <IconButton size="small" onClick={() => onSelectDevice(device.deviceId)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
